import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CardUserComponent } from '@components/users/card-user/card-user.component';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from '@app/material.module';
import { inject, OnInit } from '@angular/core';
import { TechnicianApiService } from '@services/api/user/technician/technician-api.service';
import { User } from '@interfaces/user';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { Subject, Subscription, forkJoin, takeUntil, debounceTime, distinctUntilChanged, tap, timer, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ProfileComponent} from '@pages/users/technicians/profile/profile.component';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrServices } from '@services/toastr/toastr.service';
import { Technician } from '@interfaces/technician';
import { AuthService } from '@services/api/auth/auth.service';
import { Roles } from '@enums/Roles';
import { LoaderService } from '@services/loader/loader.service';
import { RoomsService } from '@services/api/rooms/rooms.service';
import { Room } from '@interfaces/room';

@Component({
  selector: 'app-technicians',
  imports: [CardUserComponent, TablerIconsModule, MaterialModule, CommonModule, ReactiveFormsModule, TranslateModule],
  standalone: true,
  templateUrl: './technicians.component.html',
  styleUrl: './technicians.component.scss'
})
export class TechniciansComponent implements OnInit{
  private technician_api_service = inject(TechnicianApiService);
  private rooms_service = inject(RoomsService);
  private toastr_service = inject(ToastrServices);
  private loadingService: LoaderService = inject(LoaderService);
  private user_api_service = inject(AuthService);
  protected user: User | null = null;
  protected dialog = inject(MatDialog);
  protected technicians: Technician[] = [];
  protected filteredTechnicians: Technician[] = [];
  private destroy$ = new Subject<void>();
  private filterInitialized = false;
  private roomsPollingSubscription: Subscription | null = null;
  private readonly roomsPollingInterval = 15000;
  searchControl = new FormControl('');

  ngOnInit(): void {
    this.loadTechnicians();
    this.getUserData();
  }

  getUserData(): void {
    this.user_api_service.get_user_data()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.user = response.data;
        },
        error: (error) => {
          console.error(error);
        }
      });
  }

  loadTechnicians(): void {
    this.technician_api_service.get_technicians_list_data()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const technicians = response.data ?? [];
          this.setTechniciansData(technicians);
        },
        error: (error) => {
          console.log(error);
          this.setTechniciansData([]);
        }
      });
  }

  setupFilter(): void {
    if (this.filterInitialized) {
      return;
    }
    this.filterInitialized = true;

    this.searchControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged(),
        tap(searchTerm => {
          const shouldFilter = !!searchTerm && searchTerm.length >= 3;
          if (shouldFilter) {
            this.loadingService.show();
          }
          this.filteredTechnicians = this.filterTechnicians(searchTerm);
          this.loadingService.hide();
        })
      )
      .subscribe();
  }

  addTechnician(obj: any): void{
    const dialogRef = this.dialog.open(ProfileComponent, {
      autoFocus: false,
      width: '50vw',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.toastr_service.showSuccess('Technician added succesfully', 'Success');
        this.loadTechnicians();
      }
    }, (error) => {
      console.log(error);
      this.toastr_service.showError('Impossible to add Technician', 'Error');
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.stopRoomsPolling();
    this.destroy$.complete();
  }

  protected readonly Roles = Roles;

  private setTechniciansData(technicians: Technician[]): void {
    this.technicians = technicians;
    this.filteredTechnicians = this.filterTechnicians(this.searchControl.value);
    this.setupFilter();
    this.manageRoomsPolling();
  }

  private filterTechnicians(searchTerm: string | null | undefined): Technician[] {
    if (!this.technicians.length) {
      return [];
    }

    if (!searchTerm || searchTerm.length < 3) {
      return [...this.technicians];
    }

    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    return this.technicians.filter(technician => {
      return (
        (technician.email && technician.email.toLowerCase().includes(lowerCaseSearchTerm)) ||
        (technician.name && technician.name.toLowerCase().includes(lowerCaseSearchTerm)) ||
        (technician.lastname && technician.lastname.toLowerCase().includes(lowerCaseSearchTerm)) ||
        (technician.company && technician.company.name && technician.company.name.toString().toLowerCase().includes(lowerCaseSearchTerm))
      );
    });
  }

  private mapRoomsWithConnections(rooms: Room[], connections: any): Room[] {
    const connectList = Array.isArray(connections) ? connections : [];

    return rooms.map(room => {
      const connectedItem = connectList.find((item: any) => item && item['connectionId'] === room.uuid);
      return {
        ...room,
        connected: !!connectedItem,
        connectionNumber: connectedItem ? connectedItem['connectionNumber'] : 0
      };
    });
  }

  private mapTechniciansWithConnections(technicians: Technician[], rooms: Room[]): Technician[] {
    return technicians.map(technician => {
      const activeRoom = rooms.find(room => room.connected && room.technician && room.technician.id === technician.id) ?? null;
      const connectionNumber = activeRoom?.connectionNumber ?? 0;

      return {
        ...technician,
        connected: !!activeRoom,
        connectionNumber,
        activeRoom
      };
    });
  }

  private manageRoomsPolling(): void {
    if (this.technicians.length) {
      this.startRoomsPolling();
    } else {
      this.stopRoomsPolling();
    }
  }

  private startRoomsPolling(): void {
    if (this.roomsPollingSubscription && !this.roomsPollingSubscription.closed) {
      return;
    }

    this.roomsPollingSubscription = timer(0, this.roomsPollingInterval)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          if (!this.technicians.length) {
            return of(null);
          }

          return forkJoin({
            rooms: this.rooms_service.getRooms(false),
            connections: this.rooms_service.getRoomActive(false)
          }).pipe(
            catchError(error => {
              console.error(error);
              return of(null);
            })
          );
        })
      )
      .subscribe(result => {
        if (!result) {
          return;
        }

        const mappedRooms = this.mapRoomsWithConnections(result.rooms?.data ?? [], result.connections);
        this.technicians = this.mapTechniciansWithConnections(this.technicians, mappedRooms);
        this.filteredTechnicians = this.filterTechnicians(this.searchControl.value);
      });
  }

  private stopRoomsPolling(): void {
    if (this.roomsPollingSubscription) {
      this.roomsPollingSubscription.unsubscribe();
      this.roomsPollingSubscription = null;
    }
  }
}
